import { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { UserPicker } from '@/components/common/UserPicker';
import { cn } from '@/lib/utils';

/**
 * InlineOwnerEdit — Double-click to edit an owner field using UserPicker.
 *
 * @param {object} props
 * @param {string} props.value - Current owner value in "Name <email>" format
 * @param {(newValue: string) => Promise<void>} props.onSave - Async save handler receiving "Name <email>"
 * @param {string} [props.className] - Additional classes
 * @param {React.ReactNode} [props.children] - Custom display content
 */
export function InlineOwnerEdit({ value, onSave, className, children }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const startEdit = () => setEditing(true);
  const cancel = () => setEditing(false);

  const handleSelect = async (user) => {
    const newValue = `${user.name} <${user.email}>`;
    if (newValue === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(newValue);
      setEditing(false);
    } catch {
      // Keep editing on error
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div
        onDoubleClick={startEdit}
        className={cn('cursor-default group', className)}
        title="Doble clic para editar"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') startEdit();
        }}
        aria-label={`Editar responsable: ${value || 'vacío'}`}>
        {children}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <div className="flex-1">
        <UserPicker value="" onSelect={handleSelect} onClear={cancel} placeholder="Buscar persona..." autoOpen />
      </div>
      {value && (
        <button
          onClick={async () => {
            setSaving(true);
            try {
              await onSave('');
              setEditing(false);
            } catch {
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
          className="h-7 px-2 rounded text-xs text-destructive hover:bg-destructive/10 shrink-0"
          aria-label="Quitar responsable">
          Quitar
        </button>
      )}
      <button
        onClick={cancel}
        disabled={saving}
        className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:bg-muted shrink-0"
        aria-label="Cancelar">
        <HugeiconsIcon icon={Cancel01Icon} className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
